import { useState } from "react";
import { type ImageCache, imageCache } from "./ImageCache";
import { selectImageSource } from "./selectImageSource";
import {
  BASE_RETRY_DELAY_MS,
  MAX_RETRY_DELAY_MS,
  MAX_THUMBNAIL_RETRIES,
  useBackoffImageLoader,
} from "./useBackoffImageLoader";

export { BASE_RETRY_DELAY_MS, MAX_RETRY_DELAY_MS, MAX_THUMBNAIL_RETRIES };

export interface UseImageWithThumbnailOptions {
  /**
   * false 인 동안에는 캐시를 acquire 하지 않는다. 기본값 true.
   *
   * 뷰포트 밖이거나 lazy-loading 대기 중인 이미지에 대해 변환·디코드 비용을 회피.
   */
  enabled?: boolean;
  /** 원본 src 를 HEIC 로 다뤄 변환 경로로 보낼지. thumbnailSrc 에는 적용하지 않는다. */
  isHeic?: boolean;
  /** 사용할 캐시 인스턴스. 기본은 모듈 싱글톤. 테스트나 격리 컨텍스트에서만 명시 주입. */
  cache?: ImageCache;
}

export interface UseImageWithThumbnailResult {
  /**
   * 표시 가능한 URL.
   *
   * - 캐시가 사전 디코드까지 완료한 경우에만 채워진다 (`status === "ready"`).
   * - thumbnail 재시도 중이면 null (loading 으로 처리).
   * - thumbnail 재시도 소진 후 원본 src 로 fallback 했고 그것도 실패면 error.
   */
  src: string | null;
  isLoading: boolean;
  /** thumbnail 도 src 도 모두 실패한 경우의 에러. */
  error: Error | null;
  /** thumbnail 재시도 소진으로 원본 src 로 낙착됐는지. UI 가 보통 알 필요는 없음. */
  isFallback: boolean;
}

/**
 * 비동기 server thumbnail (Celery 처리) 가 도착하지 않은 동안 retry 하면서,
 * 소진 시 원본 src 로 자동 fallback 하는 캐시 구독 훅.
 *
 * 두 책임을 합성한다:
 * - {@link selectImageSource}: "thumbnail vs 원본 중 무엇을 로드할지" 순수 결정.
 * - {@link useBackoffImageLoader}: "그 단일 URL 을 backoff 재시도로 끈질기게 로드".
 *
 * thumbnail 재시도가 소진되면 loader 가 `onExhausted` 로 통보하고, 그 신호로 원본 src 로
 * 낙착한다 (isFallback=true). thumbnailSrc 가 변경되면 소진 상태가 render 단계에서 리셋된다.
 */
export function useImageWithThumbnail(
  src: string,
  thumbnailSrc: string | undefined,
  options?: UseImageWithThumbnailOptions,
): UseImageWithThumbnailResult {
  const { enabled = true, isHeic, cache = imageCache } = options ?? {};

  const [exhausted, setExhausted] = useState(false);

  // thumbnailSrc 변경 시 소진 상태를 render 단계에서 동기 리셋.
  const [prevThumbnailSrc, setPrevThumbnailSrc] = useState(thumbnailSrc);
  if (prevThumbnailSrc !== thumbnailSrc) {
    setPrevThumbnailSrc(thumbnailSrc);
    setExhausted(false);
  }

  const selection = selectImageSource({ src, thumbnailSrc, exhausted });

  const {
    src: loadedSrc,
    isLoading,
    error,
  } = useBackoffImageLoader(selection.targetSrc, {
    enabled,
    isHeic: selection.useThumbnail ? false : isHeic,
    retryOnError: selection.useThumbnail,
    onExhausted: () => setExhausted(true),
    cache,
  });

  return {
    src: loadedSrc,
    isLoading,
    error,
    isFallback: selection.isFallback,
  };
}
